import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  SquaresFour,
  PresentationChart,
  ClipboardText,
  Folders,
  ChartLineUp,
  DropHalf,
  Boat,
  Notebook,
  Anchor,
  Compass,
  UsersThree,
  Lifebuoy,
  GearSix,
  ClockCounterClockwise,
  SignOut,
  Circle,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import constant from "../../constant";
import { MODERN_ICON } from "../../constants/modernIcons";
import { useCurrentWorkingVessel } from "../../customHooks/useCurrentWorkingVessel";
import { useCan } from "../../hooks/useCan";
import { usePermissions } from "../../context/PermissionContext";
import { useUserDetails } from "../../hooks/useUserDetails";
import { getDesignationName, isMasterRank } from "../../utils/marpolRoleAccess";

const SIDEBAR_COLLAPSED_KEY = "sidebarCollapsed";
const RAIL_ICON = MODERN_ICON.rail;

const RAIL_GROUPS = [
  {
    id: "main",
    names: ["Dashboard", "Reports", "My Reports", "Other Reports", "KPI"],
  },
  {
    id: "operations",
    names: ["MARPOL", "Reporting", "Logs", "Voyage", "Vessels", "Crew Members", "Support"],
  },
  {
    id: "system",
    names: ["Settings", "Activity & IP Log"],
  },
];

const RAIL_TOOLTIPS = {
  "Activity & IP Log": "Audit Trail",
  "Crew Members": "Crew",
};

const buildRailGroups = (items) => {
  const remaining = [...items];
  const groups = [];

  for (const group of RAIL_GROUPS) {
    const groupItems = group.names
      .map((name) => {
        const idx = remaining.findIndex((item) => item.name === name);
        if (idx === -1) return null;
        return remaining.splice(idx, 1)[0];
      })
      .filter(Boolean);

    if (groupItems.length > 0) {
      groups.push({ id: group.id, items: groupItems });
    }
  }

  if (remaining.length > 0) {
    groups.push({ id: "other", items: remaining });
  }

  return groups;
};

const isSidebarCollapsed = () =>
  document.body.classList.contains("sidebar-collapsed") ||
  localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";

const setSubmenuDrawerOpen = (open) => {
  const wrapper = document.getElementById("layout-wrapper");
  if (!wrapper) return;
  if (open) {
    wrapper.classList.add("active");
  } else {
    wrapper.classList.remove("active");
  }
};

const getStoredVesselId = () => {
  try {
    const saved = localStorage.getItem(constant.keys.selectedVessel);
    if (!saved) return null;
    const v = JSON.parse(saved);
    return v.value ?? v.id ?? null;
  } catch {
    return null;
  }
};

/**
 * ✅ Reusable Sidebar Component
 * @param {Array} sidebarOptions - Array of { name, url, icon }
 */

const CommonSidebar = ({ }) => {
  const { vessel } = useCurrentWorkingVessel();
  const { user } = useUserDetails();
  const { permissions, roleName } = usePermissions();

  const adminPerm = useCan("superadmin.dashboard");
  const ownerPerm = useCan("owner.dashboard");
  const crewPerm = useCan("crew.dashboard");
  const isCrewProfile =
    !adminPerm.canView &&
    (crewPerm.canView || String(roleName || "").toLowerCase() === "crew");
  const isMasterProfile =
    !adminPerm.canView &&
    !ownerPerm.canView &&
    isMasterRank(getDesignationName(user));

  const location = useLocation();
  const navigate = useNavigate();

  const [activeMenu, setActiveMenu] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // 🔍 Added for search input
  const [filteredSubmenu, setFilteredSubmenu] = useState([]);
  const [openRightItem, setOpenRightItem] = useState(null);
  const [pendingFlyout, setPendingFlyout] = useState(null);
  const menuNavRef = React.useRef(null);

  useEffect(() => {
    if (location.pathname.startsWith("/access-control")) {
      setOpenRightItem("Roles & Access");
    } else if (location.pathname.startsWith("/settings/support")) {
      setOpenRightItem("Support");
    } else if (
      location.pathname.startsWith("/user")
      || location.pathname.startsWith("/settings/users/crew")
    ) {
      setOpenRightItem("Users");
    }
  }, [location.pathname, activeMenu]);

  /* ======================================================
       🔹 Permission Helpers (SAFE)
    ====================================================== */

  const hasPermission = (screenKey) => {
    return permissions?.[screenKey]?.view === true;
  };

  const hasAnyVesselPermission = () => {
    const vesselScreens = [
      "vessel.management",
      "vessel.group",
      "vessel.owner",
      "vessel.master-data",
      "vessel.assignment",
    ];

    return vesselScreens.some((key) => hasPermission(key));
  };

  /**
   * Super Admin is read-only: strip out any "New Entry" submenu children
   * and retarget each parent to its History / List route so the nav
   * doesn't lead into a creation form.
   */
  const isReadOnlyAdmin = adminPerm.canView;

  const stripCreateItems = (submenu) => {
    if (!Array.isArray(submenu)) return submenu;
    return submenu
      .map((item) => {
        if (!item) return item;
        const children = Array.isArray(item.children)
          ? item.children.filter((c) => c && c.name !== "New Entry")
          : item.children;

        // If the parent only had a "New Entry" landing url, retarget it
        // to the first remaining child (typically History / List).
        const fallbackUrl = children?.[0]?.url || item.url;

        return {
          ...item,
          url: fallbackUrl,
          children,
        };
      })
      .filter((item) => {
        if (!item) return false;
        // Drop submenu items that *only* offered a New Entry shortcut.
        if (item.name === "New Entry") return false;
        return true;
      });
  };
  useEffect(() => {
    document.body.classList.add("sidebar-enable", "vertical-collpsed");
    const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    document.body.classList.toggle("sidebar-collapsed", collapsed);
    localStorage.removeItem("activeMenu");
  }, []);

  const closeSubmenuFlyout = () => {
    document.getElementById("layout-wrapper")?.classList.remove("active");
  };

  const openSubmenuFlyout = () => {
    document.getElementById("layout-wrapper")?.classList.add("active");
  };

  const getOpenSubmenuParent = (items, menuName = activeMenu, pendingName = pendingFlyout) =>
    items.find((m) => m.submenu && (m.name === menuName || m.name === pendingName));

  // ✅ Left sidebar items
  const getMenuItems = () => {

    const items = [
      {
        name: "Dashboard",
        Icon: SquaresFour,
        url: constant.Authedcomponent.dashboard.url,
      },


    ];

    if (adminPerm.canView) {
      items.push({
        name: "Reports",
        Icon: PresentationChart,
        url: constant.Authedcomponent.adminVesselReports.url,
      });
    }

    if (isCrewProfile) {
      items.push({
        name: "My Reports",
        Icon: ClipboardText,
        url: constant.Authedcomponent.myDailyReports.url,
      });
    }
    // ---------- MARPOL submenu (defined as data so we can strip
    // "New Entry" children for Super Admin) ----------
    const marpolSubmenu = [
      {
        name: "Oil Record Part I",
        url: constant.Authedcomponent.marpolOrbPart1NewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolOrbPart1NewEntry.url },
          // Approvals (vessel-side roles only — C/E & Master).
          // Super Admin is excluded; History stays underneath.
          ...(isReadOnlyAdmin
            ? []
            : [{ name: "Approvals", url: constant.Authedcomponent.marpolOrbPart1Approvals.url }]),
          { name: "History",   url: constant.Authedcomponent.marpolOrbPart1History.url },
        ],
      },
      {
        name: "Oil Record Part II",
        url: constant.Authedcomponent.marpolOrbPart2NewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolOrbPart2NewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolOrbPart2History.url },
        ],
      },
      {
        name: "Garbage Record Book",
        url: constant.Authedcomponent.marpolGarbageNewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolGarbageNewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolGarbageHistory.url },
        ],
      },
      {
        name: "Ballast Water Record",
        url: constant.Authedcomponent.marpolBallastNewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolBallastNewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolBallastHistory.url },
        ],
      },
      {
        name: "Biofouling Record Book",
        url: constant.Authedcomponent.marpolBiofoulingNewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolBiofoulingNewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolBiofoulingHistory.url },
        ],
      },
      {
        name: "Annex IV Record",
        url: constant.Authedcomponent.marpolAnnexIVNewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolAnnexIVNewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolAnnexIVHistory.url },
        ],
      },
      {
        name: "Annex VI Record",
        url: constant.Authedcomponent.marpolAnnexVINewEntry.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.marpolAnnexVINewEntry.url },
          { name: "History",   url: constant.Authedcomponent.marpolAnnexVIHistory.url },
        ],
      },
      {
        name: "MARPOL Master Data",
        url: "/marpol-master",
      },
    ];

    items.push(
      {
        name: "Other Reports",
        Icon: Folders,
        submenu: [
          { name: "Operation Reports", url: constant.Authedcomponent.createReportList.url },
        ],
      },
      {
        name: "KPI",
        Icon: ChartLineUp,
        url: constant.Authedcomponent.kpiDashboard.url,
      },
      {
        name: "MARPOL",
        Icon: DropHalf,
        submenu: isReadOnlyAdmin ? stripCreateItems(marpolSubmenu) : marpolSubmenu,
      }
    );

    const logsSubmenu = [
      {
        name: "Deck Log",
        url: constant.Authedcomponent.deckLog.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.deckLog.url },
          { name: "List",      url: constant.Authedcomponent.deckLogList.url },
        ],
      },
      {
        name: "Engine Log",
        url: constant.Authedcomponent.engineLogCreate.url,
        children: [
          { name: "New Entry", url: constant.Authedcomponent.engineLogCreate.url },
          { name: "List",      url: constant.Authedcomponent.noonReportList.url },
        ],
      },
    ];

    if (isCrewProfile) {
      const elogsVesselId = vessel?.vessel_id ?? getStoredVesselId();
      items.push({
        name: "Reporting",
        Icon: Boat,
        url: constant.Authedcomponent.vesselDashboard.url,
        ...(elogsVesselId ? { state: { vessel_id: elogsVesselId } } : {}),
      });
    }

    items.push({
      name: "Logs",
      Icon: Notebook,
      submenu: isReadOnlyAdmin ? stripCreateItems(logsSubmenu) : logsSubmenu,
    });

    // Voyage: Master/Captain — port reports; Super Admin — route plan editor.
    const voyageNavState = vessel?.vessel_id
      ? { data: vessel, vessel_id: vessel.vessel_id }
      : undefined;

    if (isMasterProfile) {
      items.push({
        name: "Voyage",
        Icon: Compass,
        url: constant.Authedcomponent.voyageRoutePlan.url,
        state: voyageNavState,
        submenu: [
          {
            name: "Route Plan",
            url: constant.Authedcomponent.voyageRoutePlan.url,
            state: voyageNavState,
          },
          {
            name: "Port Report (Departure)",
            url: constant.Authedcomponent.startVoyage.url,
            state: voyageNavState,
          },
          {
            name: "SOSP Report",
            url: constant.Authedcomponent.sospReportCreate.url,
            state: voyageNavState,
          },
          {
            name: "EOSP Report",
            url: constant.Authedcomponent.eospReportCreate.url,
            state: voyageNavState,
          },
          {
            name: "Port Report (Arrival)",
            url: constant.Authedcomponent.portArrivalReportCreate.url,
            state: voyageNavState,
          },
        ],
      });
    } else if (isReadOnlyAdmin) {
      items.push({
        name: "Voyage",
        Icon: Compass,
        url: constant.Authedcomponent.voyageRoutePlan.url,
      });
    }

    /* 🔥 Vessel Section → Only if ANY vessel permission */
    if (hasAnyVesselPermission()) {
      items.push({
        name: "Vessels",
        Icon: Anchor,
        submenu: [
          hasPermission("vessel.management") && {
            name: "Vessels",
            url: constant.Authedcomponent.listVessel.url,
          },
          hasPermission("vessel.group") && {
            name: "Vessel Groups",
            url: constant.Authedcomponent.listVesselGroup.url,
          },
          hasPermission("vessel.owner") && {
            name: "Vessel Owner",
            url: constant.Authedcomponent.listVesselOwner.url,
          },
          {
            name: 'Vessel Master Data',
            url: '',
            children: [
              { name: "Ship Type", url: constant.Authedcomponent.listShipType.url },
              { name: "Ship Purpose", url: constant.Authedcomponent.listShipPurpose.url },
              { name: "Activity Type", url: constant.Authedcomponent.listActivityTypeCategory.url },
              { name: "Activity Purpose", url: constant.Authedcomponent.listActivityType.url },
            ],
          },
        ].filter(Boolean),
      });
    }

    items.push({
      name: "Crew Members",
      Icon: UsersThree,
      url: constant.Authedcomponent.listCrew.url,
      submenu: [
        { name: "On Board", url: constant.Authedcomponent.listCrew.url },
        { name: "Crew History", url: `${constant.Authedcomponent.listCrew.url}?view=history` },
      ],
    },)
    const isAdminProfile = adminPerm.canView;

    if (isCrewProfile) {
      items.push({
        name: "Support",
        Icon: Lifebuoy,
        url: constant.Authedcomponent.ticketList.url,
        submenu: [
          { name: "Ticket List", url: constant.Authedcomponent.ticketList.url },
          { name: "Create Ticket", url: constant.Authedcomponent.createTicket.url },
        ],
      },)
    } else if (isAdminProfile) {
      items.push({
        name: "Settings",
        Icon: GearSix,
        submenu: [
          {
            name: "Users",
            url: constant.Authedcomponent.listUser.url,
            children: [
              { name: "User List", url: constant.Authedcomponent.listUser.url },
              { name: "Create User", url: constant.Authedcomponent.addUser.url },
              { name: "Crew List", url: constant.Authedcomponent.listCrew.url },
            ],
          },
          {
            name: "Master Information",
            url: constant.Authedcomponent.accessControl.url,
            children: [
              { name: "Roles & Access", url: constant.Authedcomponent.accessControl.url },
              { name: "UN/LOCODE", url: constant.Authedcomponent.listUNLocode.url },
              { name: "Sea State", url: constant.Authedcomponent.listSeaState.url },
            ],
          },
          { name: "System Config", url: constant.Authedcomponent.systemConfig.url },
          { name: "SSO & Identity", url: constant.Authedcomponent.ssoSettings.url },
        ],
      },)
    }

    if (adminPerm.canView || ownerPerm.canView || crewPerm.canView) {
      items.push({
        name: "Activity & IP Log",
        Icon: ClockCounterClockwise,
        url: constant.Authedcomponent.adminActivityLog.url,
      });
    }

    return items;
  };

  const menuItems = React.useMemo(
    () => getMenuItems(),
    [vessel, permissions, roleName, user, adminPerm.canView, ownerPerm.canView, crewPerm.canView, isCrewProfile, isMasterProfile]
  );

  const railGroups = React.useMemo(() => buildRailGroups(menuItems), [menuItems]);

  const railTooltip = (name) => RAIL_TOOLTIPS[name] || name;

  const renderRailItem = (item, itemKey) => {
    const hasSubmenu = !!item.submenu;
    const belongsToRoute = routeBelongsToMenu(item, fullLocation);
    const isFlyoutOpen = hasSubmenu && activeMenu === item.name;
    const hasOpenSubmenuFlyout = menuItems.some(
      (m) => m.submenu && activeMenu === m.name
    );
    const isDirectLink = !hasSubmenu && !!item.url;
    const isDirectActive = isDirectLink && belongsToRoute && !hasOpenSubmenuFlyout;
    const isSubmenuActive =
      hasSubmenu && (isFlyoutOpen || (belongsToRoute && activeMenu === item.name));
    const isCurrent = isDirectActive || isSubmenuActive;
    const IconComp = item.Icon || Circle;
    const tooltip = railTooltip(item.name);

    return (
      <li key={itemKey} className={isCurrent ? "current" : ""}>
        <Link
          to={item.url || "#"}
          state={item.state || {}}
          className={`waves-effect iship-rail-tooltip ${isCurrent ? "active" : ""}`}
          data-tooltip={tooltip}
          aria-label={tooltip}
          onClick={(e) => {
            if (hasSubmenu) e.preventDefault();
            handleMenuClick(item.name, hasSubmenu, item.url, item.submenu);
          }}
        >
          <IconComp
            className="leftIconbar"
            {...RAIL_ICON}
            aria-hidden="true"
          />
          <p>{item.name}</p>
        </Link>
      </li>
    );
  };

  /** Expand the matching submenu group when the current route belongs to it. */
  const modulePrefix = (url) => {
    if (!url || url === "#") return "";
    const parts = url.split("/").filter(Boolean);
    if (parts.length < 2) return url;
    return `/${parts.slice(0, 2).join("/")}`;
  };

  /** Prefix-safe active check so e.g. /ticket does not match /ticket/list when list is a sibling. */
  const pathIsUnderNavUrl = (navUrl, pathOnly) => {
    if (!navUrl || navUrl === "#") return false;
    const base = navUrl.split("?")[0];
    if (pathOnly === base) return true;
    if (!pathOnly.startsWith(`${base}/`)) return false;
    const baseSegs = base.split("/").filter(Boolean);
    // Single segment (e.g. /crew) must not match siblings like /crew/list.
    if (baseSegs.length === 1) return false;
    return true;
  };

  const isKpiRoute = (pathOnly) => pathOnly === "/kpi" || pathOnly.startsWith("/kpi/");

  const menuItemOwnsRoute = (item, pathOnly) => {
    if (item?.name === "KPI" && isKpiRoute(pathOnly)) return true;
    return item?.url ? pathIsUnderNavUrl(item.url, pathOnly) : false;
  };

  const isNavLinkActive = (url, currentLocation, peerUrls = []) => {
    if (!url || !currentLocation) return false;

    if (url.includes("?")) {
      return currentLocation === url;
    }

    const pathOnly = currentLocation.split("?")[0];
    if (pathOnly === url) return true;
    if (!pathIsUnderNavUrl(url, pathOnly)) return false;

    return !peerUrls.some((peer) => {
      if (!peer || peer === url) return false;
      if (peer.includes("?")) return currentLocation === peer;
      const peerPath = currentLocation.split("?")[0];
      return peerPath === peer || peerPath.startsWith(`${peer}/`);
    });
  };

  /** Leaf submenu row — exact match; path-only rows yield to query-specific siblings. */
  const isSubmenuLeafActive = (item, currentLocation, siblingUrls = []) => {
    if (!item?.url || item.url === "#") return false;

    if (item.url.includes("?")) {
      return currentLocation === item.url;
    }

    const pathOnly = currentLocation.split("?")[0];
    const base = item.url.split("?")[0];
    const pathMatches =
      pathOnly === base || pathOnly.startsWith(`${base}/`);
    if (!pathMatches) return false;

    if (currentLocation.includes("?")) {
      const exactQuerySibling = siblingUrls.some(
        (u) => u && u !== item.url && u.includes("?") && u === currentLocation
      );
      if (exactQuerySibling) return false;
    }

    return true;
  };

  const getSubmenuSiblingUrls = (submenu) =>
    (submenu || []).map((s) => s.url).filter(Boolean);

  const submenuMatchesRoute = (submenu, currentLocation) => {
    const siblingUrls = getSubmenuSiblingUrls(submenu);
    return (submenu || []).some((sub) =>
      routeInSubmenuItem(sub, currentLocation, siblingUrls)
    );
  };

  const routeInSubmenuItem = (item, currentLocation, siblingUrls = []) => {
    if (!item) return false;

    if (Array.isArray(item.children) && item.children.length > 0) {
      const childUrls = item.children.map((c) => c?.url).filter(Boolean);
      return item.children.some(
        (c) => c?.url && isNavLinkActive(c.url, currentLocation, childUrls)
      );
    }

    return isSubmenuLeafActive(item, currentLocation, siblingUrls);
  };

  const findMenuOwnerForRoute = (items, currentLocation, preferredMenuName = null) => {
    const pathOnly = currentLocation.split("?")[0];

    const menuOwnsRoute = (item) => submenuMatchesRoute(item?.submenu, currentLocation);

    if (preferredMenuName) {
      const preferred = items.find((m) => m.name === preferredMenuName);
      if (preferred && menuOwnsRoute(preferred)) {
        return preferredMenuName;
      }
    }

    for (const item of items) {
      if (item.url && pathOnly === item.url) {
        return null;
      }
      if (menuOwnsRoute(item)) {
        return item.name;
      }
    }
    return null;
  };

  const routeBelongsToMenu = (item, currentLocation) => {
    const pathOnly = currentLocation.split("?")[0];
    if (menuItemOwnsRoute(item, pathOnly)) return true;
    return !!submenuMatchesRoute(item.submenu, currentLocation);
  };

  const findDirectMenuItem = (items, pathOnly) =>
    items.find((item) => item.url && !item.submenu && menuItemOwnsRoute(item, pathOnly));

  const fullLocation = (() => {
    const base = `${location.pathname}${location.search}`;
    if (location.pathname === "/access-control" && !location.search) {
      return "/access-control?section=permissions";
    }
    return base;
  })();

  const resolveSubmenuLandingUrl = (submenu) => {
    if (!Array.isArray(submenu) || submenu.length === 0) return null;

    for (const item of submenu) {
      if (!item) continue;
      if (item.url) return item.url;
      if (Array.isArray(item.children)) {
        const preferred = item.children.find(
          (c) => c?.url && (c.name === "History" || c.name === "List")
        );
        if (preferred?.url) return preferred.url;
        const firstChild = item.children.find((c) => c?.url);
        if (firstChild?.url) return firstChild.url;
      }
    }
    return null;
  };

  const submenuOwnsRoute = (submenu, currentLocation) =>
    submenu?.some((sub) => {
      const siblingUrls = getSubmenuSiblingUrls(submenu);
      return routeInSubmenuItem(sub, currentLocation, siblingUrls);
    }) ?? false;

  const handleMenuClick = (menuName, hasSubmenu, url, submenu) => {
    if (hasSubmenu) {
      const newActive = activeMenu === menuName ? null : menuName;

      if (newActive) {
        menuNavRef.current = newActive;
        setPendingFlyout(newActive);
        openSubmenuFlyout();
        setSearchTerm("");
        setOpenRightItem(null);
        setActiveMenu(newActive);

        const subItems = submenu || [];
        const landingUrl = resolveSubmenuLandingUrl(subItems);
        if (landingUrl && !submenuOwnsRoute(subItems, fullLocation)) {
          const landingItem = subItems.find((s) => s?.url === landingUrl);
          navigate(landingUrl, { state: landingItem?.state });
        }
        return;
      }

      menuNavRef.current = null;
      setPendingFlyout(null);
      closeSubmenuFlyout();
      setActiveMenu(null);
    } else if (url) {
      menuNavRef.current = null;
      setPendingFlyout(null);
      setActiveMenu(null);
      closeSubmenuFlyout();
      setSearchTerm("");
      setOpenRightItem(null);
    }
  };

  const prevPathRef = React.useRef(location.pathname);

  /* Sync flyout to the current route when the URL changes */
  useEffect(() => {
    const pathOnly = location.pathname;
    const pathChanged = prevPathRef.current !== pathOnly;
    prevPathRef.current = pathOnly;

    if (pathChanged && !menuNavRef.current) {
      setPendingFlyout(null);
    }

    if (findDirectMenuItem(menuItems, pathOnly)) {
      if (pathChanged && !getOpenSubmenuParent(menuItems)) {
        setActiveMenu(null);
        closeSubmenuFlyout();
      }
      return;
    }

    const preferred = menuNavRef.current || pendingFlyout || activeMenu;
    const owner = findMenuOwnerForRoute(menuItems, fullLocation, preferred);
    if (owner) {
      setActiveMenu(owner);
      setSubmenuDrawerOpen(true);
      if (menuNavRef.current) {
        menuNavRef.current = null;
      }
      return;
    }
    if (pathChanged) {
      setActiveMenu(null);
      closeSubmenuFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  /* Re-sync after menu items load (permissions) */
  useEffect(() => {
    const pathOnly = location.pathname;
    if (findDirectMenuItem(menuItems, pathOnly)) {
      if (!getOpenSubmenuParent(menuItems)) {
        setActiveMenu(null);
        closeSubmenuFlyout();
      }
      return;
    }
    const preferred = menuNavRef.current || pendingFlyout || activeMenu;
    const owner = findMenuOwnerForRoute(menuItems, fullLocation, preferred);
    if (owner) {
      setActiveMenu(owner);
      setSubmenuDrawerOpen(true);
      if (menuNavRef.current) {
        menuNavRef.current = null;
      }
    } else if (!pendingFlyout && !activeMenu) {
      setActiveMenu(null);
      closeSubmenuFlyout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]);

  /* User clicked a submenu header (e.g. MARPOL) before picking a page */
  useEffect(() => {
    if (!pendingFlyout) return;
    setActiveMenu(pendingFlyout);
    openSubmenuFlyout();
  }, [pendingFlyout]);

  /* Clear stale submenu search when switching left-rail sections */
  useEffect(() => {
    setSearchTerm("");
  }, [activeMenu]);

  useEffect(() => {
    const submenu = menuItems.find((m) => m.name === activeMenu)?.submenu || [];
    const siblingUrls = getSubmenuSiblingUrls(submenu);
    const match = submenu.find((item) => routeInSubmenuItem(item, fullLocation, siblingUrls));
    if (match) setOpenRightItem(match.name);
  }, [fullLocation, activeMenu, menuItems]);


  // 🔍 Filter submenu items on search.
  // NOTE: `menuItems` is included so the right rail refreshes when
  // permissions load asynchronously (e.g. Super Admin's "New Entry"
  // children are stripped only after the role resolves).
  useEffect(() => {
    const submenu = menuItems.find((m) => m.name === activeMenu)?.submenu || [];
    if (searchTerm.trim() === "") {
      setFilteredSubmenu(submenu);
    } else {
      const filtered = submenu.filter((s) =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubmenu(filtered);
    }
  }, [searchTerm, activeMenu, menuItems]);

  return (
    <div className="parent-sidebar iship-icon-rail">
      <nav className="vertical-menu iship-rail-shell" aria-label="Main navigation">
        <div id="sidebar-menu">
          <ul className="iship-rail-list" id="side-menu">
            {railGroups.map((group, groupIndex) => (
              <React.Fragment key={group.id}>
                {groupIndex > 0 && (
                  <li className="iship-rail-divider" aria-hidden="true" />
                )}
                {group.items.map((item, index) =>
                  renderRailItem(item, `${group.id}-${index}`)
                )}
              </React.Fragment>
            ))}

            <li className="iship-rail-spacer" aria-hidden="true" />

            <li className="text-danger iship-rail-logout">
              <a
                href={constant.commoncomponent.logout.url}
                className="waves-effect iship-rail-tooltip"
                data-tooltip="Logout"
                aria-label="Logout"
              >
                <SignOut
                  className="leftIconbar"
                  {...RAIL_ICON}
                  aria-hidden="true"
                />
                <p className="text-danger">Logout</p>
              </a>
            </li>
          </ul>
        </div>
      </nav>

      <div className="vertical-right-menu iship-submenu-panel">
        <div className="vertical-right-inner">
          {activeMenu && (
            <div className="iship-submenu-head">
              <span className="iship-submenu-title">{activeMenu}</span>
            </div>
          )}
          <div className="search-section-box">
            <div className="input-group mt-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search submenu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="input-group-text bg-white border-start-0">
                <MagnifyingGlass {...MODERN_ICON.sm} aria-hidden="true" />
              </span>
            </div>
          </div>
          <ul>
            {filteredSubmenu.length > 0 ? (
              filteredSubmenu.map((item, i) => {
                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const isOpen = openRightItem === item.name;
                const childUrls = hasChildren ? item.children.map((c) => c.url).filter(Boolean) : [];
                const siblingUrls = filteredSubmenu.map((s) => s.url).filter(Boolean);
                const pathOnly = fullLocation.split("?")[0];
                const childActive = hasChildren && item.children.some(
                  (c) => isNavLinkActive(c.url, fullLocation, childUrls)
                );
                const itemPathOnly = (item.url || "").split("?")[0];
                const parentDirectActive =
                  !childActive &&
                  (hasChildren
                    ? fullLocation === item.url || pathOnly === itemPathOnly
                    : isSubmenuLeafActive(item, fullLocation, siblingUrls));
                const isExpanded = hasChildren && (isOpen || childActive);

                return (
                  <li key={i} className={parentDirectActive ? "iship-submenu-active" : ""}>
                    <div
                      className={`right-parent ${isExpanded ? "open" : ""} ${parentDirectActive ? "active" : ""} ${childActive ? "has-active-child" : ""}`}
                      onClick={() =>
                        hasChildren && setOpenRightItem(isExpanded ? null : item.name)
                      }
                    >
                      <Link
                        to={item.url}
                        state={item.state}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isSidebarCollapsed()) closeSubmenuFlyout();
                        }}
                      >
                        {item.name}
                      </Link>
                    </div>

                    {hasChildren && isExpanded && (
                      <ul className="right-child">
                        {item.children.map((child, c) => {
                          const childIsActive = isNavLinkActive(child.url, fullLocation, childUrls);
                          return (
                          <li key={c} className={childIsActive ? "iship-submenu-active" : ""}>
                            <Link
                              to={child.url}
                              state={child.state}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isSidebarCollapsed()) closeSubmenuFlyout();
                              }}
                              className={childIsActive ? "active" : ""}
                            >
                              {child.name}
                            </Link>
                          </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="text-muted text-center mt-3">
                No matching results
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CommonSidebar;
